import { QueryManager } from '@/packages'
import { PaginationIndicator } from '@codeleap/web'
import React, { useCallback, useMemo } from 'react'

type UseQueryListOptions = {
  noMoreItemsText?: string
  forceLoading?: boolean
  forceRefreshing?: boolean
  forceHasNextPage?: boolean
  disableFetching?: boolean
}

export function useQueryList<T extends { id: string | number }, F>(
  list: ReturnType<QueryManager<T, F>['useList']>,
  options: UseQueryListOptions = {}
) {
  const {
    noMoreItemsText = 'No more items to show',
    forceHasNextPage = false,
    forceLoading = false,
    forceRefreshing = false,
    disableFetching = false
  } = options

  const { items, query } = list

  const isEmpty = useMemo(() => items?.length <= 0 || !items?.length, [items?.length])

  const hasMore = useMemo(() => {
    return isEmpty ? false : query.isLoading || query.hasNextPage
  }, [query.isLoading, query.hasNextPage, isEmpty])

  const showPaginationIndicator = isEmpty ? false : query.isFetchingNextPage || !hasMore

  const ListPaginationIndicator = useCallback(() => {
    if (!showPaginationIndicator) return null

    return (
      <PaginationIndicator
        isFetching={query.isFetchingNextPage || forceHasNextPage}
        noMoreItemsText={items.length > 0 ? undefined : noMoreItemsText}
        hasMore={hasMore}
      />
    )
  }, [hasMore, query.isFetchingNextPage, showPaginationIndicator])

  const totalItems = items?.length || 0

  return {
    data: items as T[],
    totalItems,
    fetchNextPage: () => {
      if (!hasMore || disableFetching) return
      query.fetchNextPage?.()
    },
    keyExtractor: item => item?.id,
    onRefresh: query.refetch,
    refreshing: forceRefreshing || query.isRefetching,
    loading: forceLoading || query?.isLoading,
    ListFooterComponent: ListPaginationIndicator,

    isFetchingNextPage: query.isFetchingNextPage,
    ListLoadingIndicatorComponent: () => null,
    hasNextPage: hasMore,
    isLoading: query.isLoading,
    placeholder: {
      loading: (query.isFetching || query?.isLoading) && !query?.isRefetching
    },
    showPaginationIndicator
  }
}

export type UseQueryListReturn<T extends QueryItem, F> = ReturnType<typeof useQueryList<T, F>>
